import * as am4core from '@amcharts/amcharts4/core';
import { addLicense as am5Licence } from '@amcharts/amcharts5';
import { Component, inject, OnInit, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { WebsocketService } from './classes/_services/_websocket.service';
import { AuthService } from './classes/_services/auth.service';
import { UrlService } from './classes/_services/url.service';
import { HotjarTrackingService } from './classes/_services/hotjar-tracking.service';
import { AccessControlStore } from './classes/stores/access-control-store';
import { NavbarVerticalComponent } from './shared-components/layout-components/navbar-vertical/navbar-vertical.component';
import { Toast } from 'primeng/toast';

export const browserRefresh = false;

const AM5_CHART_LICENCE = 'AM5C-1337-8745-0401-1142';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [UrlService],
  standalone: true,
  imports: [RouterOutlet, NavbarVerticalComponent, Toast]
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private router = inject(Router);
  public urlService = inject(UrlService);
  private titleService = inject(Title);
  private authService = inject(AuthService);
  public websocketService = inject(WebsocketService);
  public accessControlStore = inject(AccessControlStore);
  private hotjarTrackingService = inject(HotjarTrackingService);

  public title = 'water-utilities';
  public previousUrl: string = null;
  public currentUrl: string = null;
  public received = [];

  public hideNavbar = true;
  private hiddenNavbarRoutes = [
    '/login',
    '/register',
    '/error',
    '/confirm-registration',
    '/confirm-registration/:username',
    '/forgot-password',
    '/forgot-password/:email',
    '/auth/callback',
    '/logout'
  ];

  constructor() {
    this.titleService.setTitle('StormHarvester | ' + environment.customerIdentifier);
    this.authService.activated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((allowed: boolean) => {
        this.hideNavbar = !allowed || this.isOnHiddenRoute(this.router.url);
        const hasSpillWebsocketFeature =
          environment.hasSpillWebsocketFeature ?? environment.hasSpillsEnabled;
        const hasRealtimeAlertWebsocket =
          environment.hasRealtimeAlertWebsocket ?? hasSpillWebsocketFeature;

        if (allowed && hasSpillWebsocketFeature) {
          this.websocketService.connectSpills();
        } else {
          this.websocketService.disconnectSpills();
        }

        if (allowed && hasRealtimeAlertWebsocket) {
          this.websocketService.connectAlerts();
        } else {
          this.websocketService.disconnectAlerts();
        }
        // Load access control when user is authenticated (e.g. after login or on refresh when already logged in)
        if (allowed && !this.accessControlStore.isLoaded()) {
          this.accessControlStore.loadAccessControl();
        }
        if (allowed) {
          this.hotjarTrackingService.init();
        }
      });
    localStorage.setItem('navBarSearch', '');
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        this.hideNavbar = this.isOnHiddenRoute(event.urlAfterRedirects);
      });
    am4core.addLicense('CH1337-8745-0401-1142');
    am5Licence(AM5_CHART_LICENCE);
    // Access control is loaded when auth becomes active (see activated$ subscription above),
    // so we never call the endpoint before the user is authenticated (avoiding 401 on fresh login).
  }

  public ngOnInit() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
        this.urlService.setPreviousUrl(this.previousUrl);
      });
  }

  private isOnHiddenRoute(url: string): boolean {
    return this.hiddenNavbarRoutes.some(path => url.startsWith(path));
  }
}
